// src/common/utils/path-utils.js
// Path manipulation utilities

/**
 * Compute the common base path from all file paths
 * @param {Array} files - Array of file objects with path property
 * @returns {string} - Common base path
 */
export function getCommonBasePath(files) {
  if (!files || files.length === 0) return '';
  
  const paths = files
    .map(f => f.path || f.file_path)
    .filter(p => p && typeof p === 'string');
  
  if (paths.length === 0) return '';
  if (paths.length === 1) {
    // Single file - use its directory
    const parts = paths[0].split(/[\\/]/);
    return parts.slice(0, -1).join('/');
  }
  
  // Normalize paths to use forward slashes
  const normalized = paths.map(p => p.replace(/\\/g, '/'));
  
  // Split into parts
  const allParts = normalized.map(p => p.split('/'));
  
  // Find common prefix
  const firstParts = allParts[0];
  let commonParts = [];
  
  for (let i = 0; i < firstParts.length - 1; i++) { // -1 to exclude filename
    const part = firstParts[i];
    if (allParts.every(parts => parts[i] === part)) {
      commonParts.push(part);
    } else {
      break;
    }
  }
  
  return commonParts.join('/');
}

/**
 * Get relative path from a base path
 * @param {string} fullPath - Full file path
 * @param {string} basePath - Base path to compute relative to
 * @returns {string} - Relative path, or just filename if base doesn't match
 */
export function getRelativePath(fullPath, basePath) {
  if (!fullPath) return '';
  if (!basePath) {
    // No base path - return just filename
    return fullPath.split(/[\\/]/).pop() || fullPath;
  }
  
  const normalizedPath = fullPath.replace(/\\/g, '/').replace(/\/+$/, '');
  const normalizedBase = basePath.replace(/\\/g, '/').replace(/\/+$/, '');
  const pathForCheck = normalizedPath.toLowerCase();
  const baseForCheck = normalizedBase.toLowerCase();

  if (pathForCheck === baseForCheck) {
    return normalizedPath.split('/').pop() || normalizedPath;
  }

  if (pathForCheck.startsWith(`${baseForCheck}/`)) {
    const relative = normalizedPath.slice(normalizedBase.length).replace(/^\/+/, '');
    if (relative) return relative;
  }
  
  // Fallback to just filename
  return fullPath.split(/[\\/]/).pop() || fullPath;
}

/**
 * Get display name for a file with folder hierarchy
 * @param {Object} file - File object
 * @param {string} basePath - Common base path
 * @returns {string} - Display name with folder structure
 */
export function getFileDisplayName(file, basePath = '') {
  if (!file) return '';
  
  const path = file.path || file.file_path;
  if (path) {
    return getRelativePath(path, basePath);
  }
  
  // Fallback to other properties
  if (file.displayLabel) return file.displayLabel;
  if (file.title) return file.title;
  if (file.name) return file.name;
  if (file.file_id) return `File ${file.file_id}`;
  
  return '';
}







